// src/components/CreateSpace/Step2/components/ImageLoader.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./styles.module.scss";
import { CustomInput, MessageBox } from "reusableComponents";
import { uploadImageIcon } from "assets/icons";
import { useTranslation } from "react-i18next";

const ImageLoader = ({
  index,
  formValues,
  onImageChange,
  onDescriptionChange,
  label = "Upload your image",
  required = false,
  errorMessage = null,
  imageKey,
  descriptionKey,
  descriptionLabel,
  maxSizeMB = 10,
  compressing = false,
  disabled = false,
  "data-error": dataError,
}) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [localError, setLocalError] = useState(null);
  const descriptionInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { t } = useTranslation("global");

  // Derived values
  const finalImageKey = imageKey || `image_${index}`;
  const finalDescriptionKey = descriptionKey || `image_${index}_description`;

  // In ImageLoader.jsx, add this useEffect at the top of the component
useEffect(() => {
  console.log(`🖼️ [${finalImageKey}] Component state:`, {
    hasFormValue: !!formValues[finalImageKey],
    hasPreview: !!imagePreview,
    formValueType: formValues[finalImageKey] instanceof File ? 'File' : typeof formValues[finalImageKey],
    isCompressing: compressing
  });
}, [formValues[finalImageKey], imagePreview, finalImageKey, compressing]);

  // Handle initial image load and form value changes
  useEffect(() => {
    const file = formValues[finalImageKey];
    if (!file) {
      setImagePreview(null);
      return;
    }

    if (file instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else if (typeof file === 'string' && file.startsWith('http')) {
      // If it's a URL string (from server), use it directly
      setImagePreview(file);
    } else {
      setImagePreview(null);
    }
  }, [formValues, finalImageKey]);

  // Clear error when image is successfully loaded
  useEffect(() => {
    if (formValues[finalImageKey] && localError) {
      setLocalError(null);
    }
  }, [formValues, finalImageKey, localError]);

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      onImageChange?.(finalImageKey, null, index);
      setImagePreview(null);
      return;
    }

    // Validate file type
    if (!file.type || !file.type.startsWith('image/')) {
      setLocalError(t('validation.invalidImageType') || 'Please upload a valid image file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (basic check - the hook will do deeper compression)
    if (file.size > maxSizeMB * 1024 * 1024) { // 10MB max for upload
      setLocalError(t('validation.fileTooLarge', { size: maxSizeMB}) || `File is too large. Maximum size is ${maxSizeMB}MB.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      // Create preview immediately for better UX
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setLocalError(null);
      
      // Pass the original file to parent - let the hook handle compression
      onImageChange?.(finalImageKey, file, index);
      
    } catch (error) {
      console.error("Image processing failed:", error);
      setLocalError(t('errors.imageProcessingFailed') || 'Failed to process image. Please try again.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDescriptionChange = (event) => {
    const value = event.target.value;
    onDescriptionChange?.(finalDescriptionKey, value);
  };

 const handleRemoveImage = (e) => {
  e.stopPropagation();
  e.preventDefault();
  
  console.log(`🗑️ [${finalImageKey}] Remove button clicked`);
  console.log(`   Current form value:`, formValues[finalImageKey]);
  console.log(`   Current preview:`, imagePreview);
  
  // Clear local preview
  setImagePreview(null);
  
  // Reset file input
  if (fileInputRef.current) {
    fileInputRef.current.value = '';
    console.log(`   Reset file input`);
  }
  
  // Clear local error
  setLocalError(null);
  
  // IMPORTANT: Call parent handler with null
  if (onImageChange) {
    console.log(`   Calling onImageChange with null`);
    onImageChange(finalImageKey, null, index);
  } else {
    console.error(`   ❌ onImageChange is not defined!`);
  }
};

  const handleContainerClick = () => {
    if (!disabled && fileInputRef.current && !compressing) {
      fileInputRef.current.click();
    }
  };

  const handleKeyDown = (e) => {
    if (!disabled && (e.key === 'Enter' || e.key === ' ') && fileInputRef.current && !compressing) {
      e.preventDefault();
      fileInputRef.current.click();
    }
  };

  const hasImage = formValues[finalImageKey] || imagePreview;

  return (
    <div 
      className={styles.imageInputContainer}
      data-error={dataError}
    >
      {label && (
        <label className={styles.imageLabel}>
          {label}
          {required && <span className={styles.required}>{t("provide.step2.pictures.required")}</span>}
        </label>
      )}

      <div
        className={`${styles.imagePreviewContainer} ${compressing ? styles.compressing : ''}`}
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={hasImage ? "Change image" : "Upload image"}
        aria-busy={compressing}
      >
        {compressing ? (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner}></div>
            <span className={styles.loadingText}>
              {t('common.compressing') || 'Optimizing...'}
            </span>
          </div>
        ) : imagePreview ? (
          <>
            <img
              src={imagePreview}
              alt={`Preview ${index}`}
              className={styles.imagePreview}
            />
            <button
              type="button"
              className={styles.removeButton}
              onClick={handleRemoveImage}
              aria-label="Remove image"
              disabled={disabled}
            >
              ✕
            </button>
          </>
        ) : (
          <div className={styles.imagePlaceholder}>
            <span className={styles.addImageButton}>
              <img src={uploadImageIcon} alt="Upload icon" />
            </span>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          id={`image-upload-${index}`}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className={styles.imageInput}
          autoComplete="off"
          disabled={disabled || compressing}
        />
      </div>

      <CustomInput
        ref={descriptionInputRef}
        value={formValues[finalDescriptionKey] || ""}
        placeholder={descriptionLabel || "Image description"}
        handleChange={handleDescriptionChange}
        required={required}
        labelAbove={true}
        type="text"
        autoComplete="off"
        disabled={disabled || !hasImage}
      />
      
      {(localError || errorMessage) && (
        <MessageBox 
          message={localError || errorMessage} 
          type="error" 
          onClose={() => setLocalError(null)}
          closable={!!localError}
        />
      )}
    </div>
  );
};

export default ImageLoader;